import type { NostrSigner } from '@nostrify/types';
import { NSecSigner, NBrowserSigner, NConnectSigner } from '@nostrify/nostrify';
import type { NConnectSignerOpts } from '@nostrify/nostrify';

import { signPsbtLocal } from '@/lib/bitcoin';

// ---------------------------------------------------------------------------
// BtcSigner interface
// ---------------------------------------------------------------------------

/**
 * A Nostr signer extended with Bitcoin PSBT signing capability.
 *
 * Implementations receive a hex-encoded unsigned PSBT, sign all Taproot
 * inputs whose `tapInternalKey` matches the signer's key, and return the
 * hex-encoded signed (but not finalized) PSBT.
 */
export interface BtcSigner extends NostrSigner {
  signPsbt(psbtHex: string): Promise<string>;
}

/** Runtime check for whether a signer supports `signPsbt`. */
export function hasBtcSigning(signer: NostrSigner): signer is BtcSigner {
  return typeof (signer as BtcSigner).signPsbt === 'function';
}

// ---------------------------------------------------------------------------
// NSecSignerBtc — local nsec signing
// ---------------------------------------------------------------------------

/**
 * Extends `NSecSigner` with local Taproot PSBT signing.
 *
 * `NSecSigner` stores the secret key in a JS `#private` field that subclasses
 * cannot access. To work around this, the constructor accepts the raw secret
 * key bytes, passes them to `super()`, and keeps its own copy in a true
 * runtime-private `#secretKeyBytes` field so the key is not reachable via
 * property enumeration or reflection on the instance.
 */
export class NSecSignerBtc extends NSecSigner implements BtcSigner {
  readonly #secretKeyBytes: Uint8Array;

  constructor(secretKey: Uint8Array) {
    super(secretKey);
    this.#secretKeyBytes = new Uint8Array(secretKey);
  }

  async signPsbt(psbtHex: string): Promise<string> {
    const privateKeyHex = Buffer.from(this.#secretKeyBytes).toString('hex');
    return signPsbtLocal(psbtHex, privateKeyHex);
  }
}

// ---------------------------------------------------------------------------
// NBrowserSignerBtc — NIP-07 extension signing
// ---------------------------------------------------------------------------

/**
 * Extends `NBrowserSigner` with NIP-07 `window.nostr.signPsbt()` support.
 *
 * Calls the extension's `signPsbt` method if available. If the extension does
 * not expose `signPsbt`, an error is thrown with a user-friendly message.
 */
export class NBrowserSignerBtc extends NBrowserSigner implements BtcSigner {
  constructor(opts?: { timeout?: number }) {
    super(opts);
  }

  async signPsbt(psbtHex: string): Promise<string> {
    // NIP-BC §NIP-07: window.nostr.signPsbt(psbtHex: string): Promise<string>
    // Input:  lowercase hex-encoded BIP-174 PSBT
    // Output: signed (but not finalized) PSBT, also lowercase hex
    const nostr = (globalThis as {
      nostr?: {
        signPsbt?: (psbt: string) => Promise<string>;
      };
    }).nostr;

    if (!nostr) {
      throw new Error('Browser extension not available.');
    }

    if (typeof nostr.signPsbt !== 'function') {
      throw new Error(
        'This browser extension does not support Bitcoin PSBT signing (signPsbt). ' +
        'Try a NIP-BC compatible extension, or log in with your nsec key to sign locally.',
      );
    }

    const result = await nostr.signPsbt(psbtHex);

    if (typeof result !== 'string' || !result) {
      throw new Error('Browser extension returned an invalid signPsbt result.');
    }

    return result;
  }
}

// ---------------------------------------------------------------------------
// NConnectSignerBtc — NIP-46 remote signer
// ---------------------------------------------------------------------------

/**
 * Heuristics for detecting whether a NIP-46 `sign_psbt` error reflects a
 * missing-capability rejection (e.g. "method not supported", "unknown
 * command") versus a transient operational failure (network, user rejection,
 * malformed input). We have to match on strings because NIP-46 errors are
 * plain strings without structured codes.
 */
const CAPABILITY_ERROR_PATTERNS = [
  /unknown\s+(method|command)/i,
  /not\s+(implemented|supported|found)/i,
  /unsupported\s+method/i,
  /method\s+not\s+found/i,
  /invalid\s+method/i,
  /no\s+such\s+method/i,
];

function looksLikeCapabilityError(msg: string): boolean {
  return CAPABILITY_ERROR_PATTERNS.some((re) => re.test(msg));
}

/**
 * Internal type shim to access `NConnectSigner`'s private `cmd` method.
 * TypeScript only allows `protected` members to be called from subclasses, but
 * Nostrify marks `cmd` as `private`. The cast is safe: we're calling the same
 * method the parent class uses internally for every NIP-46 RPC command.
 */
type NConnectSignerInternal = {
  cmd(method: string, params: string[]): Promise<string>;
};

export class NConnectSignerBtc extends NConnectSigner implements BtcSigner {
  constructor(opts: NConnectSignerOpts) {
    super(opts);
  }

  async signPsbt(psbtHex: string): Promise<string> {
    // NIP-BC §NIP-46: sign_psbt command (mirrors the existing NIP-46 RPC pattern)
    //
    //   method:  "sign_psbt"
    //   params:  [ <psbt-hex> ]   — lowercase hex-encoded BIP-174 PSBT binary
    //   result:  <signed-psbt-hex> — signed but NOT finalized; finalization is
    //                                the client's responsibility (finalizePsbt)
    //
    // The signer signs inputs whose tapInternalKey matches the user-pubkey,
    // applying the BIP-341 key-path-only TapTweak internally.
    //
    // Error handling: NIP-46 errors arrive as plain strings. If the bunker
    // doesn't implement sign_psbt it will respond with a capability error
    // (e.g. "unknown method", "method not found"). `looksLikeCapabilityError`
    // detects this so the caller can flip to 'unsupported' via
    // `reportSignerUnsupported` — preventing repeated failed attempts.
    try {
      const result = await (this as unknown as NConnectSignerInternal).cmd('sign_psbt', [psbtHex]);

      if (typeof result !== 'string' || !result) {
        throw new Error('Remote signer returned an invalid sign_psbt result.');
      }

      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (looksLikeCapabilityError(msg)) {
        // Re-throw with a recognisable message so `isSignerCapabilityError`
        // in useBitcoinSigner correctly classifies this as a capability failure
        // and the UI can fall back to the "unsupported" state.
        throw new Error(`sign_psbt: method not supported by this remote signer. (${msg})`);
      }
      throw err;
    }
  }
}
